// WP-1.1 (§18.5 Account form): name · type · currency (create-only — locked after first
// transaction, AC-2) · platform group · opening balance · spendable flag (AC-4).
import {
  ACCOUNT_TYPES,
  CURRENCIES,
  parseAmountInput,
  type AccountType,
  type CurrencyCode,
} from '@pfos/engine';
import type { AccountResponse } from '@pfos/shared';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { ApiError } from '../lib/api';
import { useCreateAccount, useEditAccount } from '../lib/accounts';
import { AmountField, checkboxClass, Field, inputClass, selectClass } from './FormKit';
import { FormSheet } from './FormSheet';

const TYPE_LABELS: Record<AccountType, string> = {
  bank: 'Bank',
  psp_wallet: 'PSP wallet',
  cash: 'Cash',
  mobile_wallet: 'Mobile wallet',
  custom: 'Custom',
};

const CURRENCY_CODES = Object.keys(CURRENCIES) as CurrencyCode[];

interface FormValues {
  name: string;
  type: AccountType;
  currency: CurrencyCode;
  platformGroup: string;
  spendable: boolean;
}

function defaultsFor(account: AccountResponse | undefined): FormValues {
  return {
    name: account?.name ?? '',
    type: account?.type ?? 'bank',
    currency: account?.currency ?? 'PKR',
    platformGroup: account?.platformGroup ?? '',
    spendable: account?.spendable ?? true,
  };
}

export function AccountForm({
  open,
  onOpenChange,
  account,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Omit for create mode; pass the account being edited for edit mode. */
  account?: AccountResponse;
}): React.JSX.Element {
  const isEdit = account !== undefined;
  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm<FormValues>({ defaultValues: defaultsFor(account) });
  const [openingAmount, setOpeningAmount] = useState('');
  const [error, setError] = useState<string | null>(null);
  const createMutation = useCreateAccount();
  const editMutation = useEditAccount(account?.id ?? '');
  const currency = watch('currency');

  // Reset only when the sheet opens (not on every `account` identity change while open).
  useEffect(() => {
    if (open) {
      reset(defaultsFor(account));
      setOpeningAmount('');
      setError(null);
    }
  }, [open]);

  const onValid = handleSubmit(async (values) => {
    setError(null);
    try {
      if (isEdit) {
        await editMutation.mutateAsync({
          name: values.name,
          type: values.type,
          spendable: values.spendable,
          ...(values.platformGroup !== '' ? { platformGroup: values.platformGroup } : {}),
        });
      } else {
        let openingMinor = 0;
        if (openingAmount.trim() !== '') {
          const parsed = parseAmountInput(openingAmount, { defaultCurrency: values.currency });
          // AmountField already shows this error inline under the field — don't repeat it
          // in the form-level error banner (that's reserved for submission/API failures).
          if (!parsed.ok) return;
          openingMinor = parsed.money.minorUnits;
        }
        await createMutation.mutateAsync({
          name: values.name,
          type: values.type,
          currency: values.currency,
          openingMinor,
          openingAsOf: new Date().toISOString(),
          spendable: values.spendable,
          ...(values.platformGroup !== '' ? { platformGroup: values.platformGroup } : {}),
        });
      }
      onOpenChange(false);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not save the account');
    }
  });

  return (
    <FormSheet
      open={open}
      onOpenChange={onOpenChange}
      title={isEdit ? 'Edit account' : 'Add account'}
      submitLabel={isEdit ? 'Save' : 'Create account'}
      submitting={isEdit ? editMutation.isPending : createMutation.isPending}
      onSubmit={() => {
        void onValid();
      }}
    >
      <Field label="Name" htmlFor="acc-name" error={errors.name?.message}>
        <input
          id="acc-name"
          className={inputClass}
          autoFocus
          {...register('name', { required: 'Required' })}
        />
      </Field>
      <Field label="Type" htmlFor="acc-type">
        <select id="acc-type" className={selectClass} {...register('type')}>
          {ACCOUNT_TYPES.map((t) => (
            <option key={t} value={t}>
              {TYPE_LABELS[t]}
            </option>
          ))}
        </select>
      </Field>
      {!isEdit && (
        <Field label="Currency" htmlFor="acc-currency">
          <select id="acc-currency" className={selectClass} {...register('currency')}>
            {CURRENCY_CODES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </Field>
      )}
      <Field label="Platform group (optional)" htmlFor="acc-group">
        <input
          id="acc-group"
          className={inputClass}
          placeholder="e.g. PKR banks, Platforms"
          {...register('platformGroup')}
        />
      </Field>
      {!isEdit && (
        <Field label="Opening balance (optional)" htmlFor="acc-opening">
          <AmountField
            id="acc-opening"
            currency={currency}
            value={openingAmount}
            onChange={setOpeningAmount}
          />
        </Field>
      )}
      <label className="mb-3 flex items-center gap-2 text-sm text-ink" htmlFor="acc-spendable">
        <input
          id="acc-spendable"
          type="checkbox"
          className={checkboxClass}
          {...register('spendable')}
        />
        Count in Spendable
      </label>
      {error !== null && <p className="text-sm text-danger">{error}</p>}
    </FormSheet>
  );
}
